import { useEffect, useState } from "react";
import { PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { type PortfolioMatrixJob } from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import { useUpdatePortfolioSales } from "@/hooks/mutations/useUpdatePortfolioSales";
import { cn } from "@/lib/utils";

export const SalesCell = ({ job }: { job: PortfolioMatrixJob }) => {
  // `sales` is a list of user ids. Names/initials resolve via the same source
  // company intake uses (see TaggableUsersContext, which includes sales reps).
  const { salesOptions, userNameMap, userInitialsMap } = useTaggableUsers();
  const { mutate: updateSales } = useUpdatePortfolioSales();

  // Optimistic local selection; the matrix cache is patched on success.
  const [selected, setSelected] = useState<number[]>(job.sales);

  // Keep local selection in sync when job.sales changes from elsewhere — the
  // sales mutation patches every matrix row for the company, so sibling rows
  // would otherwise show stale chips.
  useEffect(() => {
    setSelected(job.sales);
  }, [job.sales]);

  const toggle = (id: number) => {
    const next = selected.includes(id)
      ? selected.filter((value) => value !== id)
      : [...selected, id];
    setSelected(next);
    updateSales({ companyId: job.companyId, request: { dealTeam: next } });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Single stable trigger element — swapping the trigger between renders
            confuses Radix's dismissal and leaks the closing click to the row. */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={
            selected.length > 0 ? "Edit salespeople" : "Add salesperson"
          }
          className={cn(
            "h-auto justify-start gap-0.5 p-0 hover:bg-transparent",
            selected.length === 0 &&
              "h-8 w-8 shrink-0 justify-center rounded-full border border-dashed border-slate-200 bg-slate-50/60 p-0 text-slate-400 hover:border-slate-300 hover:bg-slate-100 hover:text-slate-500",
          )}
        >
          {selected.length > 0 ? (
            <>
              {selected.slice(0, 3).map((id) => (
                <span
                  key={id}
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-slate-200"
                  title={userNameMap[id]}
                  aria-label={`Salesperson: ${userNameMap[id] ?? "Unknown"}`}
                >
                  <span className="text-[9px] font-semibold leading-none text-slate-800">
                    {userInitialsMap[id] ?? "?"}
                  </span>
                </span>
              ))}
              {selected.length > 3 && (
                <span className="ml-0.5 text-xs font-medium text-slate-500">
                  +{(selected.length - 3).toString()}
                </span>
              )}
            </>
          ) : (
            <PlusIcon
              className="h-3.5 w-3.5 shrink-0 opacity-70"
              strokeWidth={2}
              aria-hidden
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[168px]">
        {salesOptions.length === 0 ? (
          <DropdownMenuItem disabled>No sales members</DropdownMenuItem>
        ) : (
          salesOptions.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={selected.includes(option.value)}
              onCheckedChange={() => {
                toggle(option.value);
              }}
              onSelect={(event) => {
                // Keep the menu open so multiple can be toggled at once.
                event.preventDefault();
              }}
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
